import { configureAndInitializeAmplify } from '@modules/authentication/utils/configureAndInitializeAmplify';
import { FormEvent, useCallback, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useAuthSignUp } from 'v2/core/clients/authAmplify/hooks';
import { QueryStatus } from 'v2/core/repository/edge-server/types';
import { AuthError, toAuthError } from 'v2/core/services/auth/errors';
import { RegisterState } from 'v2/views/layouts/authenticationModule/authTypes';

const trackSignUpEvent = createAnalyticsTracker('signUpWithEmail');

interface SignUpInput {
  payload: RegisterState;
  onSuccess: (payload: RegisterState, otpView: boolean) => void;
  onError?: (err: AuthError) => void;
  onSsoError: () => void;
}

const useSignUp = ({
  payload,
  onSuccess,
  onError,
  onSsoError,
}: SignUpInput): [(e: FormEvent) => Promise<void>, QueryStatus] => {
  const amplifySignUp = useAuthSignUp();

  const [status, setStatus] = useState(QueryStatus.NOT_CALLED);
  const { firstName, lastName, email, role } = payload;

  const signUp = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setStatus(QueryStatus.NOT_CALLED);

      try {
        setStatus(QueryStatus.IN_PROGRESS);
        configureAndInitializeAmplify();

        trackSignUpEvent({
          eventName: 'Sign Up with Email CTA',
          email,
          firstName,
          lastName,
          role,
        });
        await amplifySignUp(payload);
        onSuccess(payload, true);
        setStatus(QueryStatus.SUCCESS_COMPLETED);
      } catch (err: unknown) {
        const authError = toAuthError(err);
        onError?.(authError);
        setStatus(QueryStatus.ERROR_COMPLETED);
      }
    },
    [
      email,
      firstName,
      lastName,
      role,
      amplifySignUp,
      payload,
      onSuccess,
      onError,
    ],
  );
  return [signUp, status];
};

export default useSignUp;
